import type {
  Place,
  PlaceChild,
  PlaceCoords,
  PlaceCountry,
  PlaceKind,
} from '@bandido/shared/types';

/**
 * Cuántos lugares pide un picker cuando no elige otro número.
 *
 * El maestro topea en 50 y su default es 10 (`PLACES_AUTOCOMPLETE_LIMITS`),
 * y client-api lo aplica cuando el front no manda nada. Diez alcanza para un
 * autocomplete de una sola familia, pero NO para estos pickers, que piden
 * varios `kinds` a la vez y los muestran agrupados: una familia densa se come
 * el cupo y las otras desaparecen enteras.
 *
 * Medido el 2026-09-03, buscando "malaga" con `kinds=[airport, station, port,
 * terminal]`: el árbol devuelve ~30 apeaderos de cercanías, y el **puerto de
 * Málaga sale en el puesto 11** — o sea que con el default el asesor no podía
 * elegirlo, mientras que en los diez primeros sí entraban un puerto de Palaos
 * (por un alias alemán) y una estación homónima en México. El corte no
 * descartaba "el siguiente mejor": descartaba la respuesta y conservaba el
 * ruido.
 *
 * Es una MITIGACIÓN, no la solución. Lo que debería mantener esta lista corta
 * y relevante es `servedBy` —"solo lugares que estos conectores sepan
 * vender"—, y hoy no filtra nada: los conectores de traslados vuelven con
 * `known: false` (sin cosechar), así que el picker recibe geografía cruda.
 * Cuando eso se resuelva, este número puede volver a bajar.
 */
export const PLACES_PICKER_LIMIT = 40;

/**
 * `name · parent.name · country.name` — sin `parent` es un país (ej.
 * "Muelle Saracen Bay · Koh Rong · Camboya"). Vive acá (no en `queries.ts`)
 * a propósito: es lógica pura sin nada de React Query, y `queries.ts`
 * importa `@frontend/config` (que usa `import.meta.env`, Vite-only) — una
 * función pura que solo formatea texto no debería arrastrar ese runtime.
 */
export function placeBreadcrumb(place: Place): string {
  return [place.name, place.parent?.name, place.country?.name]
    .filter((part): part is string => Boolean(part))
    .join(' · ');
}

/**
 * Lo que el formulario/URL guardan de una selección — un subconjunto de
 * `Place` más la miga ya armada, para no recalcularla en cada render de
 * afuera. `id` es el `plc_*` que se le pasa a rutero tal cual.
 *
 * `country` es OPCIONAL a propósito, igual que en `Place` (un lugar de tipo
 * país no tiene país "encima"). Lo agregó #2172 (autos): el `country` del
 * punto de retiro es un campo que el search de ese vertical necesita mandar
 * él mismo (rutero no lo deriva) — otros verticales pueden no leerlo nunca,
 * y eso es normal, no dead code.
 */
export interface PlaceRef {
  id: string;
  name: string;
  breadcrumb: string;
  kind: PlaceKind;
  code?: string;
  country?: PlaceCountry;
  /**
   * Dato de DISPLAY, nunca de búsqueda (#2247). El maestro las devuelve en
   * cada opción del autocomplete y antes se descartaban acá: al pedido viaja
   * el `plc_*` TAL CUAL —Bandido nunca traduce un lugar a coordenadas, eso lo
   * hace rutero por conector— y estas solo alimentan el mapa del recorrido.
   *
   * Es un ATAJO, no la fuente: sirve mientras la selección siga viva en el
   * formulario. Una búsqueda reconstruida desde la URL o desde recientes
   * llega sin ellas y las recupera resolviendo el `plc_*` comiteado contra el
   * maestro (`GET /places/:id/coords`) — misma geo, el contrato lo declara.
   *
   * OPCIONAL en el contrato: hay lugares sin geo publicada, y ahí no hay
   * mapa. Nunca se completan con las de otra selección.
   */
  coords?: PlaceCoords;
}

export function toPlaceRef(place: Place): PlaceRef {
  return {
    id: place.id,
    name: place.name,
    breadcrumb: placeBreadcrumb(place),
    kind: place.kind,
    ...(place.code ? { code: place.code } : {}),
    ...(place.country ? { country: place.country } : {}),
    ...(place.coords ? { coords: place.coords } : {}),
  };
}

/**
 * Una opción del picker: un `Place` más `nested`, que dice si vino ANIDADO en
 * los `children` de otro resultado (`group=true`).
 *
 * `nested` es explícito y no se deduce de `parent`: **casi todo resultado de
 * primer nivel también trae `parent`** (el contrato solo lo omite en los
 * países — ver `Place.parent` en `@bandido/shared/types` y `PlaceResult` en
 * `review-context/external-apis/ubikeishon/openapi.yaml`), así que inferirlo trataría a una ciudad
 * de primer nivel como hija de la opción de arriba.
 */
export interface PlaceOption extends Place {
  /** Solo `true` en los hijos aplanados; ausente en los de primer nivel. */
  nested?: boolean;
}

export type PlaceOptionGroup =
  | 'places'
  | 'airports'
  | 'stations'
  | 'ports'
  | 'points';

const PLACE_OPTION_GROUP_ORDER: Record<PlaceOptionGroup, number> = {
  places: 0,
  airports: 1,
  stations: 2,
  ports: 3,
  points: 4,
};

/**
 * Familias visuales del autocomplete. No son vocabulario de proveedor ni se
 * mandan al backend: solo agrupan la lista para que el asesor no tenga que
 * distinguir un aeropuerto de una ciudad leyendo la miga completa.
 */
export function placeKindGroup(kind: PlaceKind): PlaceOptionGroup {
  if (kind === 'airport') return 'airports';
  if (kind === 'station') return 'stations';
  if (kind === 'port' || kind === 'terminal') return 'ports';
  if (kind === 'poi' || kind === 'beach') return 'points';
  return 'places';
}

/**
 * Un hijo de `group=true` se agrupa bajo la familia de su cabecera para
 * conservar el patrón "ciudad con aeropuertos/estaciones colgando". La fila
 * igual muestra su tipo real con ícono/label; esta función solo decide el
 * encabezado de sección.
 */
export function placeOptionGroup(option: PlaceOption): PlaceOptionGroup {
  return option.nested && option.parent
    ? placeKindGroup(option.parent.kind)
    : placeKindGroup(option.kind);
}

export function placeOptionGroupLabelKey(option: PlaceOption): string {
  return `travelServices.places.groups.${placeOptionGroup(option)}`;
}

export function placeKindLabelKey(kind: PlaceKind): string {
  return `travelServices.places.kinds.${kind}`;
}

/**
 * Un hijo de `group=true` convertido a `Place` completo (hereda `parent` y
 * `country` de la ciudad) para que se pueda elegir, mostrar y convertir a
 * `PlaceRef` con el MISMO código que un resultado de primer nivel — sin un
 * tipo ni una rama de UI aparte para "es un hijo".
 */
function childAsPlace(child: PlaceChild, parent: Place): PlaceOption {
  return {
    id: child.id,
    kind: child.kind,
    name: child.name,
    ...(child.code ? { code: child.code } : {}),
    parent: { id: parent.id, kind: parent.kind, name: parent.name },
    ...(parent.country ? { country: parent.country } : {}),
    nested: true,
  };
}

/**
 * Resultados + hijos (si `group=true` los trajo) en una sola lista plana y
 * elegible. Deduplicado por `id`: un mismo lugar puede aparecer como
 * resultado de primer nivel Y como hijo de otro resultado en la misma
 * respuesta (ej. una ciudad matchea directo y, aparte, es hijo de su región
 * que también matchea) — sin el dedupe, MUI ve dos opciones con el mismo
 * `id` y `isOptionEqualToValue` deja de poder distinguirlas.
 */
export function flattenPlaceOptions(results: Place[]): PlaceOption[] {
  const flat: PlaceOption[] = results.flatMap((place) => [
    place,
    ...(place.children ?? []).map((child) => childAsPlace(child, place)),
  ]);
  const seen = new Set<string>();
  return flat.filter((place) => {
    if (seen.has(place.id)) return false;
    seen.add(place.id);
    return true;
  });
}

/**
 * Ordena por familia visual, conservando el ranking relativo dentro de cada
 * familia. Como los hijos de `group=true` heredan la familia visual del padre,
 * quedan pegados a su cabecera cuando ubikeishon los mandó así.
 */
export function sortPlaceOptionsForDisplay(
  options: PlaceOption[],
): PlaceOption[] {
  return options
    .map((option, index) => ({ option, index }))
    .sort((left, right) => {
      const byGroup =
        PLACE_OPTION_GROUP_ORDER[placeOptionGroup(left.option)] -
        PLACE_OPTION_GROUP_ORDER[placeOptionGroup(right.option)];
      return byGroup || left.index - right.index;
    })
    .map(({ option }) => option);
}

/**
 * `matched` se resalta SOLO cuando difiere de `name` — sin esto, un match
 * como "New y" → "Nueva York" con `matched: "New York"` parece un bug
 * (mostrar el mismo texto dos veces) en vez de la razón del match.
 */
export function shouldShowMatched(place: Place): boolean {
  return Boolean(
    place.matched && place.matched.toLowerCase() !== place.name.toLowerCase(),
  );
}

/**
 * Por qué el picker no tiene nada útil para mostrar ahora mismo — cuatro
 * estados EXCLUYENTES, no un booleano ni un string libre por callsite:
 *
 * - `disabled`  — sin autoridad de capacidades cargada o sin conector
 *                 configurado para el vertical (fail-closed).
 * - `error`     — la búsqueda al maestro de lugares falló.
 * - `tooShort`  — todavía no se escribió lo mínimo para buscar.
 * - `empty`     — se buscó y el maestro de lugares no devolvió nada.
 *
 * Antes esto era un ternario en el componente que confundía "disabled" con
 * "sin resultados" en el mismo `noOptionsText` — y esa rama ni siquiera era
 * alcanzable (MUI no abre el popup de un Autocomplete `disabled`).
 */
export type PlacesNoticeReason = 'disabled' | 'error' | 'tooShort' | 'empty';

export function placesNoticeReason({
  ready,
  queryLength,
  minLength,
  isError,
}: {
  ready: boolean;
  queryLength: number;
  minLength: number;
  isError: boolean;
}): PlacesNoticeReason {
  if (!ready) return 'disabled';
  if (isError) return 'error';
  if (queryLength < minLength) return 'tooShort';
  return 'empty';
}
